import { PlacementId } from './placementIds'

export const EMBROIDERY_TYPES = ['text', 'icon', 'logo'] as const

export type EmbroideryTypeKey = (typeof EMBROIDERY_TYPES)[number]

type dtcEmbroideryRuleIds =
  | 'max-text-items'
  | 'max-icon-items'
  | 'max-logo-items'
  | 'position-capacity'
  | 'sleeve-text'
  | 'one-text-per-placement'
  | 'one-mark-per-placement'
  | 'no-logo-over-sleeve-text'

type legacyDtcEmbroideryRulesIds =
  | 'logo-icon-exclusive'
  | 'icon-sleeve-only'
  | 'logo-chest-only'
  | 'text-chest-only'

export type RuleId = dtcEmbroideryRuleIds | legacyDtcEmbroideryRulesIds

/** One configured business rule: current selections in, reasons they are illegal out. */
export type RuleCheck = (items: readonly PlacedEmbroideryItem[]) => Violation[]

/**
 * Anatomy — per-garment: which placements this garment physically has, and what may land on each.
 * `offsets` is deferred; it slots back in additively.
 */
export type PlacementRule = {
  id: PlacementId
  allowedTypes: readonly EmbroideryTypeKey[]
}

/**
 * A narrowing layer applied on top of the garment's anatomy — for constraints that arrive as
 * runtime *data* rather than as flow policy.
 */
export type AnatomyConstraintOverlay = {
  allowedPlacementsByType?: Partial<Record<EmbroideryTypeKey, readonly PlacementId[]>>
  lockedPlacementByType?: Partial<Record<EmbroideryTypeKey, PlacementId>>
}

/**
 * The engine's view of a selected item. The state layer's `EmbroideryItem` is a structural
 * superset, so `EmbroideryItem[]` can be passed straight in without mapping.
 */
export type PlacedEmbroideryItem = {
  /**
   * A UUID v4, minted when the customer creates the item and stable for its whole life. Selections
   * are a list rather than one slot per type, so this is what addresses a single item — two items
   * can otherwise match in every field.
   */
  instanceId: string
  typeKey: EmbroideryTypeKey
  placementId: PlacementId // e.g. CHEST:LEFT, SLEEVE:RIGHT
}

/**
 * Everything the engine needs, assembled at the flow's composition root and handed to the provider
 * stack at mount. `rules` is the flow's rulebook — see `flows/dtcEmbroidery.ts`.
 */
export type EmbroideryConstraintSet = {
  rules: readonly RuleCheck[]
  anatomy: readonly PlacementRule[]
  anatomyOverlay?: AnatomyConstraintOverlay
  /**
   * Types the selection must include before it can be submitted. Sits here rather than inside the
   * overlay because it is the one constraint that *adds* a requirement instead of narrowing anatomy.
   */
  requiredTypes?: readonly EmbroideryTypeKey[]
}

/**
 * Not a rule violation — the selection is structurally impossible on this garment.
 */
export type StructuralReason =
  | 'PLACEMENT_NOT_ON_GARMENT'
  | 'EMBROIDERY_TYPE_NOT_ALLOWED_AT_PLACEMENT'
  | 'BLOCKED_BY_OVERLAY'

export type ViolationReason = RuleId | StructuralReason

export type Violation = {
  reason: ViolationReason
  typeKey?: EmbroideryTypeKey
  placementId?: PlacementId
  instanceIds?: readonly string[]
}

export type SelectionErrorReason = ViolationReason | 'MISSING_REQUIRED_TYPE'

export type SelectionError = Omit<Violation, 'reason'> & { reason: SelectionErrorReason }

/** Why a type or placement is unavailable to add. `'NO_PLACEMENT'` = the garment has nowhere for it. */
export type UnavailabilityReason = ViolationReason | 'NO_PLACEMENT'

export type PlacementAvailability = {
  placementId: PlacementId
  /**
   * Whether this garment offers the type here at all. Drives **hide**: `false` means the placement
   * is not a choice for this type, so the picker should not render it rather than render it disabled.
   */
  offered: boolean
  /**
   * Whether it can be selected right now. Drives **disable**: an `offered` placement with
   * `available: false` is blocked by what is already selected, and `reason` says why.
   */
  available: boolean
  reason?: UnavailabilityReason
  /** The items actually responsible for `reason`, when it names one, e.g. the icon that blocks a logo here. */
  instanceIds?: readonly string[]
}

export type TypeAvailability = {
  typeKey: EmbroideryTypeKey
  /**
   * Whether this garment offers the type *anywhere* — true if any placement is `offered`. Drives
   * whether to render the type's form at all: a garment with no logo-capable placement should not
   * show a logo form, regardless of what is currently selected. Unlike `canAdd`, it does not change
   * as the customer selects things.
   *
   * Anatomy and the overlay decide this; the flow's rules do not. A rule that forbids a type
   * everywhere leaves it `offered` with every placement unavailable, because a rule answers
   * "illegal right now", not "never on this garment". A flow that wants such a type hidden should
   * say so in the overlay.
   */
  offered: boolean
  /** Whether one more could be added right now, given the current selections. Drives disable. */
  canAdd: boolean
  reason?: UnavailabilityReason
  placements: readonly PlacementAvailability[]
}

export type AvailabilityMatrix = Record<EmbroideryTypeKey, TypeAvailability>
